import { Participant } from "@medbillai/generated-graphql-types";

import {
  answeringChatEventId,
  FieldSpec as FieldSpecSchema,
  isTextingAsk,
} from "@medbillai/schemas";

import { isPromptActive } from "../components/case-input/composerLock";
import { createLoadingAiResponseEvent } from "./loadingEvents";
import type { BaseEvent } from "./types";

/** Progress rows do not settle a reply; its named row must reach the timeline. */
export const hasUnpersistedStreamingReply = ({
  events,
  replyRowId,
  isAgentTyping,
  streamingV2Enabled,
}: {
  events: readonly Pick<BaseEvent, "id">[];
  replyRowId: string | null | undefined;
  isAgentTyping: boolean;
  streamingV2Enabled: boolean;
}): boolean =>
  streamingV2Enabled &&
  isAgentTyping &&
  (!replyRowId || !events.some(event => event.id === replyRowId));

/**
 * True when the tail is a `requestUserInput` prompt the user just answered in
 * this session (tracked in the optimistic store) and the agent follow-up hasn't
 * landed yet — keeps the "AI is writing" loader up after an answer. The answered
 * prompt is itself a SERVICE event, so the send-message anchor in
 * `withSyntheticLoadingEvent` skips it; this re-includes it.
 *
 * Keyed off the in-session optimistic answer, not server `isResolved`: a
 * resolved-but-not-answered-here tail (a cold open of a case answered elsewhere,
 * or a turn that completed without emitting a user-visible event) has no
 * follow-up coming, so anchoring on `isResolved` would spin the loader on every
 * such open. The caller bounds the optimistic case with a timeout for the rare
 * path where the answer commits but no turn runs.
 */
export const isAwaitingInputFollowUp = (
  lastEvent: BaseEvent | undefined,
  isAnsweredOptimistically: (eventId: string) => boolean,
): boolean => {
  if (lastEvent?.__typename === "issue__UserInputRequestedEvent_Type") {
    // The texting offer and its phone ask get no agent reply.
    return (
      isAnsweredOptimistically(lastEvent.id) &&
      !isTextingAsk(lastEvent.fieldSpec)
    );
  }
  // A re-raise shows the question's own card, so a tap answers the question.
  return (
    lastEvent?.__typename === "issue__AskReraisedEvent_Type" &&
    lastEvent.reraisedAsk.__typename ===
      "issue__UserInputRequestedEvent_Type" &&
    isAnsweredOptimistically(lastEvent.reraisedAsk.id)
  );
};

/**
 * Inject a synthetic loading event when the user has just acted and the agent
 * owes a reply, but no loading event exists yet (e.g. a refetch wiped the
 * cache-only placeholder). Two anchors:
 *
 * 1. A just-sent message: `lastSentMessageScrollActive` and a non-SERVICE tail
 *    that isn't a preview upload prompt card.
 * 2. A just-answered `requestUserInput` prompt (`awaitingInputFollowUp`): the
 *    prompt is a SERVICE event, so anchor 1 skips it.
 *
 * Self-terminating: once the real reply (or follow-up prompt) is the new tail,
 * neither anchor holds and the loader drops.
 */
export const withSyntheticLoadingEvent = (
  rawEvents: readonly BaseEvent[],
  lastSentMessageScrollActive: boolean,
  loadingEventKey: string | null,
  issueId: string | null,
  hasStaffParticipant: boolean,
  awaitingInputFollowUp: boolean,
): readonly BaseEvent[] => {
  if (
    !loadingEventKey ||
    !issueId ||
    hasStaffParticipant ||
    rawEvents.some(e => e.id === loadingEventKey)
  ) {
    return rawEvents;
  }
  const lastEvent = rawEvents[rawEvents.length - 1];
  const isPreviewPromptCard =
    lastEvent?.__typename === "issue__DocumentUploadedEvent_Type" &&
    Boolean(lastEvent.previewPromptMessage);
  const sentMessageAnchor =
    lastSentMessageScrollActive &&
    lastEvent?.participant !== Participant.SERVICE &&
    !isPreviewPromptCard;
  if (sentMessageAnchor || awaitingInputFollowUp) {
    return [...rawEvents, createLoadingAiResponseEvent(issueId)];
  }
  return rawEvents;
};

// An unreadable spec draws neither half; a group's answered card is its answer, and a document's is the upload row.
const splitsAnswer = (fieldSpec: unknown): boolean => {
  const parsed = FieldSpecSchema.safeParse(fieldSpec);
  return parsed.success && !["group", "document"].includes(parsed.data.type);
};

/**
 * Gives an answered prompt's answer its own row so it pins like a sent message;
 * reads `cacheEvents` so the split lands in the commit the prompt collapses.
 */
export const withAnswerRows = (
  rows: readonly BaseEvent[],
  cacheEvents: readonly BaseEvent[] | null | undefined,
  answers: Parameters<typeof isPromptActive>[2],
): readonly BaseEvent[] => {
  const split = (e: BaseEvent) =>
    e.__typename === "issue__UserInputRequestedEvent_Type" &&
    (e.isResolved === true || answers.answerFor(e.id) != null) &&
    // A typed answer is already its own chat row.
    answeringChatEventId(e.fieldSpec) === undefined &&
    splitsAnswer(e.fieldSpec) &&
    !isPromptActive(cacheEvents, e.id, answers);
  if (!rows.some(split)) {
    return rows;
  }
  return rows.flatMap(e =>
    split(e)
      ? [
          { ...e, answerSplit: true },
          { ...e, id: `${e.id}:answer`, answerOf: e.id },
        ]
      : [e],
  );
};

const eventSentAtMs = (event: { sentAt: string }): number => {
  const parsed = Date.parse(event.sentAt);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

/**
 * Keep display rows in the canonical timeline order even when local streaming
 * rows and refetched server rows are reconciled in a different insertion order.
 */
export const orderEventsBySentAt = <Event extends { sentAt: string }>(
  events: readonly Event[],
): readonly Event[] => {
  let ordered = true;
  for (let index = 1; index < events.length; index += 1) {
    if (eventSentAtMs(events[index - 1]!) > eventSentAtMs(events[index]!)) {
      ordered = false;
      break;
    }
  }
  if (ordered) {
    return events;
  }
  return events
    .map((event, index) => ({ event, index }))
    .sort((left, right) => {
      const bySentAt = eventSentAtMs(left.event) - eventSentAtMs(right.event);
      return bySentAt === 0 ? left.index - right.index : bySentAt;
    })
    .map(({ event }) => event);
};

export type KeyMappingState = {
  prevHadLoading: boolean;
  replacementId: string | null;
  userMsgOriginalKey: string | null;
  userMsgServerId: string | null;
  /** The member row before the tracked send. */
  userMsgPrevId: string | null;
  /** A named row listed under a loader can't be what replaces it. */
  listedBubbleRowId: string | null;
  /** Nor can the row that sat right above it. */
  rowAboveLoaderId: string | null;
};

export const INITIAL_KEY_MAPPING: KeyMappingState = {
  prevHadLoading: false,
  replacementId: null,
  userMsgOriginalKey: null,
  userMsgServerId: null,
  userMsgPrevId: null,
  listedBubbleRowId: null,
  rowAboveLoaderId: null,
};

/**
 * Update the key-mapping refs that keep LegendList keys stable across
 * loading → real event and optimistic → server ID transitions.
 *
 * Pure function: takes previous state and returns next state.
 */
export const updateKeyMappings = (
  prev: KeyMappingState,
  hasLoadingEvent: boolean,
  displayEvents: readonly BaseEvent[],
  bubbleRowId: string | null = null,
): KeyMappingState => {
  let {
    prevHadLoading,
    replacementId,
    userMsgOriginalKey,
    userMsgServerId,
    userMsgPrevId,
    listedBubbleRowId,
    rowAboveLoaderId,
  } = prev;

  // A send adds its row and the loader in one write: track only the row above.
  const aboveLoader = hasLoadingEvent
    ? displayEvents[displayEvents.length - 2]
    : undefined;

  // Track loading → real event replacement
  if (hasLoadingEvent) {
    prevHadLoading = true;
    replacementId = null;
    rowAboveLoaderId = aboveLoader?.id ?? null;
    if (displayEvents.some(event => event.id === bubbleRowId)) {
      listedBubbleRowId = bubbleRowId;
    }
  } else if (prevHadLoading) {
    prevHadLoading = false;
    const lastEvent = displayEvents[displayEvents.length - 1];
    const namedRowId = bubbleRowId === listedBubbleRowId ? null : bubbleRowId;
    const lastRowId =
      lastEvent?.id === rowAboveLoaderId ? null : (lastEvent?.id ?? null);
    replacementId = namedRowId ?? lastRowId;
  }
  // Apollo swaps the optimistic row for the server row in one write, even
  // after the loader left: no newer member row means the send failed.
  const sendRowLeft =
    !!userMsgOriginalKey &&
    !userMsgServerId &&
    !displayEvents.some(event => event.id === userMsgOriginalKey);
  if (sendRowLeft) {
    const serverRow = displayEvents.findLast(
      event => event.participant === Participant.USER,
    );
    if (serverRow && serverRow.id !== userMsgPrevId) {
      userMsgServerId = serverRow.id;
    } else {
      userMsgOriginalKey = null;
      userMsgPrevId = null;
    }
  } else if (
    aboveLoader?.participant === Participant.USER &&
    aboveLoader.id !== userMsgOriginalKey &&
    aboveLoader.id !== userMsgServerId
  ) {
    userMsgOriginalKey = aboveLoader.id;
    userMsgServerId = null;
    userMsgPrevId =
      displayEvents.findLast(
        event =>
          event.participant === Participant.USER && event.id !== aboveLoader.id,
      )?.id ?? null;
  }

  return {
    prevHadLoading,
    replacementId,
    userMsgOriginalKey,
    userMsgServerId,
    userMsgPrevId,
    listedBubbleRowId,
    rowAboveLoaderId,
  };
};
